import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { ArrowLeft, Printer, Plus, Trash, Boat, AirplaneTilt, Lock, Truck, CheckCircle, FileX, Download, CalendarBlank } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import { HsCodeField } from "@/components/HsCodePicker";

const STATUS_LABELS = {
  draft: "Draft",
  sealed: "Sealed",
  in_transit: "In Transit",
  arrived: "Arrived",
};

const STATUS_STYLES = {
  draft: "bg-zinc-100 text-zinc-700 border-zinc-200",
  sealed: "bg-sky-50 text-sky-700 border-sky-200",
  in_transit: "bg-amber-50 text-amber-700 border-amber-200",
  arrived: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

// Allowed forward transitions
const NEXT_STATUS = {
  draft: { label: "Seal manifest", to: "sealed", Icon: Lock },
  sealed: { label: "Mark in transit", to: "in_transit", Icon: Truck },
  in_transit: { label: "Mark arrived", to: "arrived", Icon: CheckCircle },
};

export default function ManifestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [packages, setPackages] = useState([]);
  const [available, setAvailable] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  // ASYCUDA (Jamaica Customs) XML export dialog state. Couriers fill in the
  // customs-mandatory fields (office code, vessel codes, voyage #, etc.) that
  // don't live on the Kanal manifest before downloading.
  const [asycudaOpen, setAsycudaOpen] = useState(false);
  const [asycudaForm, setAsycudaForm] = useState(null);
  const [asycudaBusy, setAsycudaBusy] = useState(false);

  const isL1 = user.role === "warehouse_super" || user.role === "warehouse_super_l1";
  const isL2 = user.role === "warehouse_super_l2";
  // Level-1 warehouse can add/remove packages but MUST NOT seal, arrive, or
  // delete a manifest. Level-2 gets full manage powers (seal + delete + bulk-add).
  const canManage = user.role === "super_admin" || user.role === "company_admin" || user.role === "dispatcher" || user.role === "warehouse" || isL2;
  const canAdvance = user.role === "super_admin" || user.role === "company_admin" || user.role === "dispatcher" || isL2;
  const canDelete = user.role === "super_admin" || user.role === "company_admin" || isL2;
  const canBulkAdd = user.role === "super_admin" || user.role === "company_admin" || user.role === "dispatcher" || isL2;
  const canAddPackages = canManage || isL1;  // L1 can still tick packages one by one

  const load = async () => {
    try {
      const [mr, pr] = await Promise.all([
        api.get(`/manifests/${id}`),
        api.get(`/manifests/${id}/packages`),
      ]);
      setM(mr.data);
      setPackages(pr.data.items || []);
    } catch (e) {
      toast.error("Failed to load manifest");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const loadAvailable = async () => {
    if (!m) return;
    // Same company, same method, not already on a manifest
    const params = { shipment_method: m.shipment_method, page_size: 200 };
    if (search) params.search = search;
    const { data } = await api.get("/packages", { params });
    const list = (data.items || []).filter((p) => !p.manifest_id || p.manifest_id === id);
    setAvailable(list);
  };

  const advanceStatus = async () => {
    const next = NEXT_STATUS[m.status];
    if (!next) return;
    const confirmMsg = next.to === "arrived"
      ? "Mark this manifest as arrived? All packages on it will be set to 'Ready for Pickup' (notify your recipients)."
      : `Set status to "${STATUS_LABELS[next.to]}"?`;
    if (!confirm(confirmMsg)) return;
    try {
      await api.patch(`/manifests/${id}`, { status: next.to });
      toast.success(`Manifest is now ${STATUS_LABELS[next.to]}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const addPackages = async () => {
    if (selected.size === 0) { toast.error("Select at least one package"); return; }
    try {
      await api.post(`/manifests/${id}/packages`, { package_ids: Array.from(selected) });
      toast.success(`Added ${selected.size} package${selected.size === 1 ? "" : "s"}`);
      setSelected(new Set());
      setAddOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removePackage = async (pid, tn) => {
    if (!confirm(`Remove ${tn} from this manifest?`)) return;
    try {
      await api.delete(`/manifests/${id}/packages/${pid}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteManifest = async () => {
    if (!confirm("Delete this manifest? Packages will be unlinked (not deleted).")) return;
    try {
      await api.delete(`/manifests/${id}`);
      toast.success("Manifest deleted");
      navigate("/manifests");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openPdf = async () => {
    try {
      const resp = await api.get(`/manifests/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Failed to load PDF");
    }
  };

  // ASYCUDA export: pre-fill the form (last-used defaults + sensible derived
  // values) then open the dialog.
  const openAsycuda = async () => {
    try {
      const { data } = await api.get(`/manifests/${id}/asycuda-defaults`);
      if (!data.package_count) {
        toast.error("Add at least one package before exporting to ASYCUDA");
        return;
      }
      setAsycudaForm(data);
      setAsycudaOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load ASYCUDA defaults");
    }
  };

  const downloadAsycuda = async () => {
    if (!asycudaForm) return;
    // Customs-required fields — fail fast with a friendly message.
    const required = [
      ["customs_office_code", "Customs Office Code"],
      ["voyage_number", "Voyage / Flight Number"],
      ["shipping_agent_code", "Shipping Agent Code"],
      ["vessel_loading_code", "Vessel Loading Code"],
      ["vessel_discharge_code", "Vessel Discharge Code"],
    ];
    for (const [k, label] of required) {
      if (!asycudaForm[k]?.trim()) {
        toast.error(`${label} is required by ASYCUDA`);
        return;
      }
    }
    setAsycudaBusy(true);
    try {
      // Persist these as the courier's preferred defaults for next time
      api.put(`/manifests/${id}/asycuda-defaults`, asycudaForm).catch(() => {});
      const resp = await api.post(
        `/manifests/${id}/asycuda.xml`,
        asycudaForm,
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${m.manifest_number}-asycuda.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("ASYCUDA XML downloaded");
      setAsycudaOpen(false);
    } catch (e) {
      // Blob responses don't auto-parse error JSON — read manually.
      let msg = "Failed to generate ASYCUDA XML";
      try {
        const text = await e.response?.data?.text?.();
        if (text) msg = JSON.parse(text).detail || msg;
      } catch { /* ignore */ }
      toast.error(msg);
    } finally {
      setAsycudaBusy(false);
    }
  };

  if (!m) return <div className="p-8 text-zinc-500" data-testid="manifest-loading">Loading...</div>;

  const next = NEXT_STATUS[m.status];
  const isDraft = m.status === "draft";

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="manifest-detail">
      <Toaster position="top-right" />

      <button onClick={() => navigate("/manifests")} className="text-xs font-semibold text-zinc-500 hover:text-zinc-900 mb-4 flex items-center" data-testid="back-to-manifests">
        <ArrowLeft size={14} className="mr-1" /> Back to manifests
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
            {m.shipment_method === "sea" ? "Ocean Freight" : m.shipment_method === "ground" ? "Ground Freight" : "Air Freight"}
          </div>
          <div className="flex items-center gap-3 mt-1">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans" }}>
              {m.manifest_number}
            </h1>
            <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold border ${STATUS_STYLES[m.status]}`} data-testid="manifest-status-badge">
              {STATUS_LABELS[m.status] || m.status}
            </span>
          </div>
          <div className="text-sm text-zinc-600 mt-2">
            {(m.origin || "—")} → {(m.destination || "—")} · {m.package_count} packages · {m.total_weight_kg?.toFixed(2)} kg ({((m.total_weight_kg || 0) * 2.20462).toFixed(2)} lb)
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={openPdf} data-testid="manifest-pdf-btn">
            <Printer size={14} weight="bold" className="mr-1.5" /> PDF
          </Button>
          {canManage && (
            <Button
              variant="outline"
              onClick={openAsycuda}
              data-testid="manifest-asycuda-btn"
              title="Export to Jamaica Customs ASYCUDA World"
            >
              <FileX size={14} weight="bold" className="mr-1.5" /> ASYCUDA XML
            </Button>
          )}
          {canAdvance && next && (
            <Button onClick={advanceStatus} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="advance-status-btn">
              <next.Icon size={14} weight="bold" className="mr-1.5" /> {next.label}
            </Button>
          )}
          {canDelete && (m.status === "draft" || m.status === "sealed") && (
            <Button variant="outline" onClick={deleteManifest} data-testid="delete-manifest-btn" className="text-red-600 hover:text-red-700">
              <Trash size={14} weight="bold" className="mr-1.5" /> Delete
            </Button>
          )}
        </div>
      </div>

      {/* Meta grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <Meta label="Carrier" value={m.carrier || "—"} />
        <Meta label={m.shipment_method === "sea" ? "Voyage" : m.shipment_method === "ground" ? "Trip" : "Flight"} value={m.voyage_or_flight || "—"} />
        <Meta label="Departure" value={m.departure_date ? new Date(m.departure_date).toLocaleDateString() : "—"} />
        <Meta label="Arrival" value={m.arrival_date ? new Date(m.arrival_date).toLocaleDateString() : "—"} />
        <Meta label="Origin" value={m.origin || "—"} />
        <Meta label="Destination" value={m.destination || "—"} />
      </div>
      {m.notes && (
        <div className="mb-6 p-4 rounded-md bg-zinc-50 border border-zinc-200 text-sm text-zinc-700" data-testid="manifest-notes">
          {m.notes}
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">
          Packages on this manifest
        </div>
        {(canAddPackages) && isDraft && (
          <div className="flex items-center gap-2">
            {canBulkAdd && (
              <BulkAddByDate
                manifestId={id}
                shipmentMethod={m.shipment_method}
                onDone={load}
              />
            )}
            <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (o) loadAvailable(); else setSelected(new Set()); }}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="add-packages-btn">
                  <Plus size={13} weight="bold" className="mr-1.5" /> Add packages
                </Button>
              </DialogTrigger>
            <DialogContent
              className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-2xl p-0 max-h-[90vh] gap-0 overflow-hidden flex flex-col"
              data-testid="add-packages-dialog"
            >
              <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-zinc-100 shrink-0">
                <DialogTitle>Add {m.shipment_method === "sea" ? "Sea" : m.shipment_method === "ground" ? "Ground" : "Air"} Packages to {m.manifest_number}</DialogTitle>
              </DialogHeader>
              <div className="px-5 sm:px-6 py-3 border-b border-zinc-100 shrink-0">
                <div className="flex gap-2">
                  <Input
                    placeholder="Search tracking, sender or recipient..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && loadAvailable()}
                    data-testid="add-pkg-search"
                  />
                  <Button variant="outline" onClick={loadAvailable} data-testid="add-pkg-search-btn">Search</Button>
                </div>
                <div className="text-[11px] text-zinc-500 mt-2">
                  Only {m.shipment_method === "sea" ? "Ocean" : m.shipment_method === "ground" ? "Ground" : "Air"} freight packages that are not already on another manifest are shown.
                </div>
              </div>
              <div className="overflow-y-auto px-5 sm:px-6 py-3 flex-1" data-testid="add-packages-body">
                {available.length === 0 ? (
                  <div className="py-10 text-center text-sm text-zinc-500">No matching packages found.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wider text-zinc-500">
                      <tr>
                        <th className="text-left py-2 w-8"></th>
                        <th className="text-left py-2 font-semibold">Tracking</th>
                        <th className="text-left py-2 font-semibold">Recipient</th>
                        <th className="text-right py-2 font-semibold">Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {available.map((p) => (
                        <tr key={p.id} className="border-t border-zinc-100" data-testid={`avail-pkg-${p.id}`}>
                          <td className="py-2">
                            <input
                              type="checkbox"
                              checked={selected.has(p.id)}
                              onChange={(e) => {
                                const s = new Set(selected);
                                if (e.target.checked) s.add(p.id); else s.delete(p.id);
                                setSelected(s);
                              }}
                              data-testid={`avail-pkg-check-${p.id}`}
                            />
                          </td>
                          <td className="py-2"><code className="text-xs font-mono bg-zinc-100 px-1.5 py-0.5 rounded">{p.tracking_number}</code></td>
                          <td className="py-2 text-zinc-700">{p.recipient_name}</td>
                          <td className="py-2 text-right text-zinc-700">
                            <div>{(p.weight_kg || 0).toFixed(2)} kg</div>
                            <div className="text-[11px] text-zinc-500">{((p.weight_kg || 0) * 2.20462).toFixed(2)} lb</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <DialogFooter className="px-5 sm:px-6 py-4 border-t border-zinc-100 bg-white shrink-0 gap-2 sm:gap-0">
                <div className="text-xs text-zinc-500 mr-auto self-center">{selected.size} selected</div>
                <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                <Button onClick={addPackages} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="save-add-packages-btn" disabled={selected.size === 0}>
                  Add {selected.size > 0 ? `${selected.size}` : ""}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="manifest-packages-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3 font-semibold">Tracking</th>
              <th className="px-5 py-3 font-semibold">Recipient</th>
              <th className="px-5 py-3 font-semibold">Customer #</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Weight</th>
              <th className="px-5 py-3 font-semibold">HS Code</th>
              {canManage && isDraft && <th className="px-5 py-3 font-semibold text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {packages.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-zinc-500">No packages on this manifest yet.</td></tr>
            ) : packages.map((p) => (
              <tr key={p.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`mpkg-row-${p.id}`}>
                <td className="px-5 py-3">
                  <Link to={`/packages/${p.id}`} className="text-xs font-mono font-semibold text-zinc-900 hover:text-[#002BF6]">
                    {p.tracking_number}
                  </Link>
                </td>
                <td className="px-5 py-3 text-zinc-800">{p.recipient_name}</td>
                <td className="px-5 py-3 text-zinc-700 text-xs">{p.customer_number || "—"}</td>
                <td className="px-5 py-3"><StatusBadge status={p.status} /></td>
                <td className="px-5 py-3 text-zinc-700">
                  <div>{(p.weight_kg || 0).toFixed(2)} kg</div>
                  <div className="text-[11px] text-zinc-500">{((p.weight_kg || 0) * 2.20462).toFixed(2)} lb</div>
                </td>
                <td className="px-5 py-3" data-testid={`mpkg-hs-${p.id}`}>
                  {p.hs_code ? (
                    <span className="font-mono text-xs font-semibold text-[#002BF6] bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
                      {p.hs_code}
                    </span>
                  ) : (
                    <span className="text-[11px] text-zinc-400 italic">uses default</span>
                  )}
                </td>
                {canManage && isDraft && (
                  <td className="px-5 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => removePackage(p.id, p.tracking_number)} data-testid={`remove-pkg-${p.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                      <Trash size={14} />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ASYCUDA (Jamaica Customs) XML export dialog */}
      <Dialog open={asycudaOpen} onOpenChange={setAsycudaOpen}>
        <DialogContent
          className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-2xl p-0 max-h-[90vh] gap-0 overflow-hidden flex flex-col"
          data-testid="asycuda-dialog"
        >
          <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-zinc-100 shrink-0">
            <DialogTitle>Export to ASYCUDA — Jamaica Customs</DialogTitle>
            <p className="text-xs text-zinc-600 mt-1 leading-relaxed">
              Fill in the customs-mandatory fields below. The generated XML
              follows the Jamaica Customs Agency Awbolds.xsd schema and can
              be uploaded directly into ASYCUDA World. Your inputs are saved
              as defaults for next time.
            </p>
          </DialogHeader>
          {asycudaForm && (
            <div className="px-5 sm:px-6 py-4 overflow-y-auto text-sm">
              {/* Section 1 — Identifiers */}
              <SectionLabel>Customs & Transport Identifiers</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <AsycudaField
                  label="Customs Office Code *"
                  hint="Jamaica Customs office (e.g. JMKIN = Kingston)"
                  value={asycudaForm.customs_office_code}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, customs_office_code: v }))}
                  maxLength={5}
                  testId="asycuda-customs-office"
                />
                <AsycudaField
                  label="Voyage / Flight Number *"
                  value={asycudaForm.voyage_number}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, voyage_number: v }))}
                  maxLength={17}
                  testId="asycuda-voyage"
                />
                <AsycudaField
                  label="BOL Type Code *"
                  hint="AWB = Air Waybill · BL = Sea Bill · HBL = House BL"
                  value={asycudaForm.bol_type_code}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, bol_type_code: v.toUpperCase() }))}
                  maxLength={3}
                  testId="asycuda-bol-type"
                />
                <AsycudaField
                  label="Reference Number"
                  value={asycudaForm.reference_number}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, reference_number: v }))}
                  maxLength={25}
                  testId="asycuda-ref-number"
                />
                <AsycudaField
                  label="Shipping Agent Code *"
                  hint="Your 13-digit TRN assigned by Jamaica Customs"
                  value={asycudaForm.shipping_agent_code}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, shipping_agent_code: v }))}
                  maxLength={17}
                  testId="asycuda-agent-code"
                />
                <AsycudaField
                  label="Shipping Agent Name"
                  value={asycudaForm.shipping_agent_name}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, shipping_agent_name: v }))}
                  maxLength={140}
                  testId="asycuda-agent-name"
                />
                <AsycudaField
                  label="Vessel Loading Code *"
                  hint="UN/LOCODE port of loading (e.g. USMIA)"
                  value={asycudaForm.vessel_loading_code}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, vessel_loading_code: v.toUpperCase() }))}
                  maxLength={5}
                  testId="asycuda-load-code"
                />
                <AsycudaField
                  label="Vessel Discharge Code *"
                  hint="UN/LOCODE port of discharge (e.g. JMKIN)"
                  value={asycudaForm.vessel_discharge_code}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, vessel_discharge_code: v.toUpperCase() }))}
                  maxLength={5}
                  testId="asycuda-discharge-code"
                />
                <AsycudaField
                  label="Carrier Code"
                  value={asycudaForm.carrier_code}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, carrier_code: v }))}
                  maxLength={17}
                  testId="asycuda-carrier-code"
                />
                <AsycudaField
                  label="Carrier Name"
                  value={asycudaForm.carrier_name}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, carrier_name: v }))}
                  maxLength={35}
                  testId="asycuda-carrier-name"
                />
              </div>

              {/* Section 2 — Goods */}
              <SectionLabel>Goods</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <AsycudaField
                  label="Package Type Code"
                  hint="UN/EDIFACT package type (default PK)"
                  value={asycudaForm.package_type_code}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, package_type_code: v.toUpperCase() }))}
                  maxLength={17}
                  testId="asycuda-package-type"
                />
                <HsCodeField
                  label="Goods HS Code"
                  hint="Search 5,200+ Jamaica ASYCUDA Manifest HS codes — applied to every package"
                  value={asycudaForm.goods_hs_code}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, goods_hs_code: v }))}
                  testId="asycuda-hs-code"
                />
              </div>

              {/* Section 3 — Notify party */}
              <SectionLabel>Notify Party (optional)</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <AsycudaField
                  label="Notify Name"
                  value={asycudaForm.notify_name}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, notify_name: v }))}
                  maxLength={140}
                  testId="asycuda-notify-name"
                />
                <AsycudaField
                  label="Notify Address"
                  value={asycudaForm.notify_address}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, notify_address: v }))}
                  maxLength={500}
                  testId="asycuda-notify-address"
                />
              </div>

              {/* Section 4 — Value segment */}
              <SectionLabel>Value Segment (optional, applied to every BOL)</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <AsycudaField
                  label="Payment Indicator"
                  hint="PP = Prepaid · CC = Collect"
                  value={asycudaForm.freight_pc_indicator}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, freight_pc_indicator: v.toUpperCase() }))}
                  maxLength={3}
                  testId="asycuda-pc-indicator"
                />
                <AsycudaField
                  label="Freight Value"
                  type="number"
                  value={asycudaForm.freight_value ?? ""}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, freight_value: v === "" ? null : Number(v) }))}
                  testId="asycuda-freight-value"
                />
                <AsycudaField
                  label="Freight Currency"
                  value={asycudaForm.freight_currency}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, freight_currency: v.toUpperCase() }))}
                  maxLength={3}
                  testId="asycuda-freight-currency"
                />
                <AsycudaField
                  label="Customs Value"
                  type="number"
                  value={asycudaForm.customs_value ?? ""}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, customs_value: v === "" ? null : Number(v) }))}
                  testId="asycuda-customs-value"
                />
                <AsycudaField
                  label="Customs Currency"
                  value={asycudaForm.customs_currency}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, customs_currency: v.toUpperCase() }))}
                  maxLength={3}
                  testId="asycuda-customs-currency"
                />
                <div />
                <AsycudaField
                  label="Insurance Value"
                  type="number"
                  value={asycudaForm.insurance_value ?? ""}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, insurance_value: v === "" ? null : Number(v) }))}
                  testId="asycuda-insurance-value"
                />
                <AsycudaField
                  label="Insurance Currency"
                  value={asycudaForm.insurance_currency}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, insurance_currency: v.toUpperCase() }))}
                  maxLength={3}
                  testId="asycuda-insurance-currency"
                />
                <div />
                <AsycudaField
                  label="Transport Value"
                  type="number"
                  value={asycudaForm.transport_value ?? ""}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, transport_value: v === "" ? null : Number(v) }))}
                  testId="asycuda-transport-value"
                />
                <AsycudaField
                  label="Transport Currency"
                  value={asycudaForm.transport_currency}
                  onChange={(v) => setAsycudaForm((f) => ({ ...f, transport_currency: v.toUpperCase() }))}
                  maxLength={3}
                  testId="asycuda-transport-currency"
                />
              </div>

              <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                <b>{asycudaForm.package_count}</b> package{asycudaForm.package_count === 1 ? "" : "s"} will be exported as House Bills (Bol_nature 23, type {asycudaForm.bol_type_code || "AWB"}).
              </div>
            </div>
          )}
          <DialogFooter className="px-5 sm:px-6 py-3 border-t border-zinc-100 shrink-0">
            <Button variant="outline" onClick={() => setAsycudaOpen(false)} data-testid="asycuda-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={downloadAsycuda}
              disabled={asycudaBusy}
              className="bg-[#002BF6] hover:bg-[#0021C7] text-white"
              data-testid="asycuda-download-btn"
            >
              <Download size={14} weight="bold" className="mr-1.5" />
              {asycudaBusy ? "Generating..." : "Download XML"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AsycudaField({ label, hint, value, onChange, maxLength, testId, type = "text" }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">
        {label}
      </Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        data-testid={testId}
        className="mt-1"
      />
      {hint && <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div
      className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500 mt-5 mb-2 pb-1 border-b border-zinc-100 first:mt-0"
    >
      {children}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-900 font-medium mt-0.5 truncate">{value}</div>
    </div>
  );
}


function BulkAddByDate({ manifestId, shipmentMethod, onDone }) {
  const [open, setOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [previewCount, setPreviewCount] = useState(null);
  const [busy, setBusy] = useState(false);

  const refreshPreview = async (from, to) => {
    try {
      const { data } = await api.get(`/manifests/${manifestId}/bulk-add-preview`, {
        params: { date_from: from || undefined, date_to: to || undefined },
      });
      setPreviewCount(data.count || 0);
    } catch (e) {
      setPreviewCount(null);
    }
  };

  useEffect(() => {
    if (open) refreshPreview(dateFrom, dateTo);
    // eslint-disable-next-line
  }, [open, dateFrom, dateTo]);

  const commit = async () => {
    if (previewCount === 0) {
      toast.message("No packages match those dates.");
      return;
    }
    if (!confirm(`Add ${previewCount} matching ${shipmentMethod} package${previewCount === 1 ? "" : "s"} to this manifest?`)) return;
    setBusy(true);
    try {
      await api.post(`/manifests/${manifestId}/bulk-add-by-date`, {
        date_from: dateFrom || null,
        date_to: dateTo || null,
      });
      toast.success(`${previewCount} package${previewCount === 1 ? "" : "s"} added`);
      setOpen(false);
      setDateFrom("");
      setDateTo("");
      setPreviewCount(null);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk add failed");
    } finally {
      setBusy(false);
    }
  };

  // Quick presets — Today / This week / Last 7 days
  const applyPreset = (preset) => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (preset === "today") {
      const t = iso(today);
      setDateFrom(t); setDateTo(t);
    } else if (preset === "this_week") {
      const start = new Date(today);
      start.setDate(today.getDate() - today.getDay()); // Sunday
      setDateFrom(iso(start));
      setDateTo(iso(today));
    } else if (preset === "last_7") {
      const start = new Date(today);
      start.setDate(today.getDate() - 6);
      setDateFrom(iso(start));
      setDateTo(iso(today));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="bulk-add-btn" title="Bulk-add all scanned packages within a date range (Level-2 only)">
          <CalendarBlank size={13} weight="bold" className="mr-1.5" /> Bulk add by date
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="bulk-add-dialog">
        <DialogHeader>
          <DialogTitle>Bulk add packages by date</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-zinc-500">
            Add every eligible <strong>{shipmentMethod}</strong> package scanned in the selected date range to this manifest.
          </p>
          <div className="flex gap-2 text-xs">
            <button type="button" onClick={() => applyPreset("today")} className="px-2.5 py-1 rounded border border-zinc-200 hover:bg-zinc-50" data-testid="bulk-preset-today">Today</button>
            <button type="button" onClick={() => applyPreset("this_week")} className="px-2.5 py-1 rounded border border-zinc-200 hover:bg-zinc-50" data-testid="bulk-preset-week">This week</button>
            <button type="button" onClick={() => applyPreset("last_7")} className="px-2.5 py-1 rounded border border-zinc-200 hover:bg-zinc-50" data-testid="bulk-preset-7">Last 7 days</button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="bulk-date-from" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="bulk-date-to" className="mt-1.5" />
            </div>
          </div>
          <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm" data-testid="bulk-preview-count">
            {previewCount === null ? (
              <span className="text-zinc-500">Pick a date range…</span>
            ) : (
              <span className="text-blue-800 font-medium">
                {previewCount} eligible package{previewCount === 1 ? "" : "s"} will be added.
              </span>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={commit} disabled={busy || !previewCount} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="bulk-add-submit">
            {busy ? "Adding…" : `Add ${previewCount || 0}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

